import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getApiErrorMessage } from '@/utils/apiError'
import { lcClassApi, memberApi, departmentApi, specialGroupApi, LcClassRequest, LcClass, LcClassMember, Member, Department, SpecialGroup, SpecialGroupMember } from '@/api/b2b'
import { tenantApi, License } from '@/api/tenant'
import * as configApi from '@/api/configuration'
import { usePageStore } from '@/store/pageStore'
import { ConfigurationSummary, ConfigurationEditModal } from '@/components/configuration'
import type { LessonConfiguration } from '@/types/configuration'
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  UserPlus,
  X,
  Search,
  ChevronDown,
  ChevronLeft,
  Calendar as CalendarIcon,
  Package,
  BookOpen,
  Settings,
  CheckCircle2,
  AlertCircle,
  Clock,
  Building2,
  UsersRound,
  Info,
} from 'lucide-react'
import { clsx } from 'clsx'

export default function ClassManagement() {
  const { t } = useTranslation('admin')
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const { setPageSubtitle } = usePageStore()
  
  // Main state
  const [selectedClass, setSelectedClass] = useState<LcClass | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  // 페이지 부제목 설정
  useEffect(() => {
    setPageSubtitle(selectedClass ? t('class.subtitle') : null)
    return () => setPageSubtitle(null)
  }, [selectedClass, setPageSubtitle])

  // 상품 관리에서 "이 상품으로 클래스 만들기" 클릭 시 처리
  useEffect(() => {
    const state = location.state as { openCreate?: boolean; licenseId?: number; productName?: string; selectClassId?: number } | null
    if (state?.openCreate && state?.licenseId) {
      // 라이선스 미리 선택하고 모달 열기, 클래스명은 상품명으로 기본 설정
      const productName = state.productName || licenses.find(l => l.id === state.licenseId)?.productName || ''
      setCreateFormData(prev => ({
        ...prev,
        licenseIds: [state.licenseId!],
        name: prev.name || productName
      }))
      setIsCreateModalOpen(true)
      // 히스토리 상태 초기화 (새로고침 시 다시 열리지 않도록)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  
  // Class selector dropdown
  const [isClassSelectorOpen, setIsClassSelectorOpen] = useState(false)

  // Create class modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createFormData, setCreateFormData] = useState<LcClassRequest>({
    name: '',
    description: '',
    maxStudents: 10,
    schedule: {},
    licenseIds: [],
    startDate: new Date().toISOString().split('T')[0],
    deadline: undefined,
    allowSelfRegistration: false,
    allowAdminAssignment: true
  })

  // Edit class modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editFormData, setEditFormData] = useState<LcClassRequest>({
    name: '',
    description: '',
    maxStudents: 10,
    schedule: {},
    licenseIds: [],
    deadline: undefined,
    allowSelfRegistration: false,
    allowAdminAssignment: true
  })

  // Settings modal
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [classSettings, setClassSettings] = useState({
    allowSelfRegistration: false,
    allowAdminAssignment: true,
  })

  // Learning period modal (시작일 + 마감일)
  const [isDeadlineModalOpen, setIsDeadlineModalOpen] = useState(false)
  const [newStartDate, setNewStartDate] = useState<string>('')
  const [newDeadline, setNewDeadline] = useState<string>('')

  // Assignment modal
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false)
  const [assignmentTarget, setAssignmentTarget] = useState<'all' | 'department' | 'specialGroup' | 'individual'>('all')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null)
  const [selectedSpecialGroupId, setSelectedSpecialGroupId] = useState<number | null>(null)
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [memberSearchTerm, setMemberSearchTerm] = useState('')
  const [selectAllChecked, setSelectAllChecked] = useState(false)

  // Cancel assignment modal
  const [isCancelAssignmentModalOpen, setIsCancelAssignmentModalOpen] = useState(false)

  // Expand seats modal
  const [isExpandSeatsModalOpen, setIsExpandSeatsModalOpen] = useState(false)
  const [newMaxStudents, setNewMaxStudents] = useState<number>(10)

  // Product modal
  const [isProductModalOpen, setIsProductModalOpen] = useState(false)
  const [selectedLicenseIds, setSelectedLicenseIds] = useState<number[]>([])

  // Configuration edit modal
  const [isConfigEditModalOpen, setIsConfigEditModalOpen] = useState(false)

  // Data queries
  const { data: classesData, isLoading } = useQuery({
    queryKey: ['lc-classes', showInactive],
    queryFn: () => showInactive ? lcClassApi.getAllIncludingInactive() : lcClassApi.getAll(),
  })

  const { data: membersData } = useQuery({
    queryKey: ['all-members'],
    queryFn: () => memberApi.getAll(),
  })

  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentApi.getAll(),
  })

  const { data: specialGroupsData } = useQuery({
    queryKey: ['special-groups'],
    queryFn: () => specialGroupApi.getAll(),
  })

  const { data: specialGroupMembersData } = useQuery({
    queryKey: ['special-group-members', selectedSpecialGroupId],
    queryFn: () => specialGroupApi.getMembers(selectedSpecialGroupId!),
    enabled: !!selectedSpecialGroupId && assignmentTarget === 'specialGroup',
  })

  const { data: licensesData } = useQuery({
    queryKey: ['all-active-licenses'],
    queryFn: () => tenantApi.getAllActiveLicenses(),
  })

  const { data: classMembersData, refetch: refetchClassMembers } = useQuery({
    queryKey: ['class-members', selectedClass?.id],
    queryFn: () => lcClassApi.getMembers(selectedClass!.id),
    enabled: !!selectedClass,
  })

  // Class Configuration 조회
  const { data: classConfiguration, isLoading: isClassConfigLoading } = useQuery({
    queryKey: ['configuration', 'class', selectedClass?.id],
    queryFn: () => configApi.getClassConfiguration(selectedClass!.id),
    enabled: !!selectedClass,
  })

  // License Configuration 조회 (Class Configuration이 없을 때 fallback용)
  // 클래스에 연결된 첫 번째 라이선스의 설정을 fallback으로 사용
  const firstLicenseId = selectedClass?.licenses?.[0]?.licenseId
  const { data: licenseConfiguration, isLoading: isLicenseConfigLoading } = useQuery({
    queryKey: ['configuration', 'license', firstLicenseId],
    queryFn: () => configApi.getLicenseConfiguration(firstLicenseId!),
    enabled: !!firstLicenseId,
  })

  // Product Configuration 조회 (License도 없을 때 fallback)
  const firstProductId = selectedClass?.licenses?.[0]?.productId
  const { data: productConfiguration, isLoading: isProductConfigLoading } = useQuery({
    queryKey: ['configuration', 'product', firstProductId],
    queryFn: () => configApi.getProductConfiguration(firstProductId!),
    enabled: !!firstProductId,
  })

  // Curriculum Configuration 조회 (Product도 없을 때 fallback)
  // licensesData에서 curriculumContentId 가져오기 (licenses 변수가 아직 정의 전이므로)
  const allLicenses: License[] = licensesData?.data?.data || []
  const firstLicense = allLicenses.find(l => l.id === firstLicenseId)
  const curriculumContentId = firstLicense?.curriculumContentId
  const { data: curriculumConfiguration, isLoading: isCurriculumConfigLoading } = useQuery({
    queryKey: ['configuration', 'curriculum', curriculumContentId],
    queryFn: () => configApi.getCurriculumConfiguration(curriculumContentId!),
    enabled: !!curriculumContentId,
    staleTime: 5 * 60 * 1000,
  })

  // Default Configuration 조회 (최종 fallback)
  const { data: defaultConfiguration, isLoading: isDefaultConfigLoading } = useQuery({
    queryKey: ['configuration', 'default'],
    queryFn: () => configApi.getDefaultConfiguration(),
    staleTime: 10 * 60 * 1000,
  })

  // Derived data
  const classes: LcClass[] = classesData?.data?.data || []

  // 상품 상세에서 클래스 클릭 시 해당 클래스 선택
  useEffect(() => {
    const state = location.state as { selectClassId?: number } | null
    if (state?.selectClassId && classes.length > 0) {
      const targetClass = classes.find(cls => cls.id === state.selectClassId)
      if (targetClass) {
        setSelectedClass(targetClass)
        // 히스토리 상태 초기화
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, classes])

  const allMembers: Member[] = membersData?.data?.data || []
  const departments: Department[] = departmentsData?.data?.data || []
  const specialGroups: SpecialGroup[] = specialGroupsData?.data?.data || []
  const licenses = allLicenses // allLicenses는 위에서 이미 정의됨
  const classMembers: LcClassMember[] = classMembersData?.data?.data || []
  
  // Available licenses (not yet linked to current class)
  const availableLicenses = licenses.filter(
    l => !selectedClass?.licenses?.some(cl => cl.licenseId === l.id)
  )

  // Members not in current class
  const membersNotInClass = allMembers.filter(
    m => !classMembers.some(cm => cm.userId === m.userId)
  )

  // Members not in ANY class (한 학습자는 1개 클래스만 소속 가능)
  const membersNotInAnyClass = allMembers.filter(
    m => !m.classNames || m.classNames.length === 0
  )

  // Helper: 다른 클래스에 소속된 멤버의 클래스 이름 반환
  const getOtherClassName = (member: Member): string | null => {
    if (!member.classNames || member.classNames.length === 0) return null
    // 현재 클래스에 소속된 경우는 제외
    const otherClasses = member.classNames.filter(cn => cn !== selectedClass?.name)
    return otherClasses.length > 0 ? otherClasses[0] : null
  }

  // Filtered members for assignment modal
  const filteredMembers = membersNotInClass.filter(member =>
    member.fullName.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(memberSearchTerm.toLowerCase())
  )

  // Mutations
  const createClassMutation = useMutation({
    mutationFn: (data: LcClassRequest) => lcClassApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['lc-classes'] })
      setIsCreateModalOpen(false)
      setCreateFormData({
        name: '',
        description: '',
        maxStudents: 10,
        schedule: {},
        licenseIds: [],
        startDate: new Date().toISOString().split('T')[0],
        deadline: undefined,
        allowSelfRegistration: false,
        allowAdminAssignment: true
      })
      const newClass = response.data.data
      if (newClass) {
        setSelectedClass(newClass)
      }
      toast.success(t('class.toast.created'))
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const updateClassMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<LcClassRequest> }) =>
      lcClassApi.update(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['lc-classes'] })
      setIsEditModalOpen(false)
      setIsSettingsModalOpen(false)
      setIsDeadlineModalOpen(false)
      setIsExpandSeatsModalOpen(false)
      const updatedClass = response.data.data
      if (updatedClass) {
        setSelectedClass(updatedClass)
      }
      toast.success(t('class.toast.updated'))
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const deleteClassMutation = useMutation({
    mutationFn: (id: number) => lcClassApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lc-classes'] })
      queryClient.invalidateQueries({ queryKey: ['all-members'] })
      setSelectedClass(null)
      toast.success(t('class.toast.deleted'))
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const addMemberMutation = useMutation({
    mutationFn: ({ classId, userIds }: { classId: number; userIds: number[] }) =>
      lcClassApi.addMembers(classId, userIds),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['class-members'] }),
        queryClient.invalidateQueries({ queryKey: ['lc-classes'] }),
        queryClient.invalidateQueries({ queryKey: ['all-members'] }),
      ])
      refetchClassMembers()
      // Refresh selectedClass to update currentStudents count
      const response = await lcClassApi.getAll()
      const updatedClass = response.data.data?.find((c: LcClass) => c.id === selectedClass?.id)
      if (updatedClass) {
        setSelectedClass(updatedClass)
      }
      setIsAssignmentModalOpen(false)
      resetAssignmentForm()
      toast.success(t('class.toast.assigned'))
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: ({ classId, userId }: { classId: number; userId: number }) =>
      lcClassApi.removeMembers(classId, [userId]),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['class-members'] }),
        queryClient.invalidateQueries({ queryKey: ['lc-classes'] }),
        queryClient.invalidateQueries({ queryKey: ['all-members'] }),
      ])
      refetchClassMembers()
      // Refresh selectedClass to update currentStudents count
      const response = await lcClassApi.getAll()
      const updatedClass = response.data.data?.find((c: LcClass) => c.id === selectedClass?.id)
      if (updatedClass) {
        setSelectedClass(updatedClass)
      }
      toast.success(t('class.toast.unassigned'))
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const addProductMutation = useMutation({
    mutationFn: ({ classId, licenseIds }: { classId: number; licenseIds: number[] }) =>
      lcClassApi.addProducts(classId, licenseIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['lc-classes'] })
      setSelectedLicenseIds([])
      // Refresh selected class data
      const response = await lcClassApi.getAll()
      const updatedClass = response.data.data?.find((c: LcClass) => c.id === selectedClass?.id)
      if (updatedClass) {
        setSelectedClass(updatedClass)
      }
      toast.success(t('class.toast.productLinked'))
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const removeProductMutation = useMutation({
    mutationFn: ({ classId, licenseIds }: { classId: number; licenseIds: number[] }) =>
      lcClassApi.removeProducts(classId, licenseIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['lc-classes'] })
      // Refresh selected class data
      const response = await lcClassApi.getAll()
      const updatedClass = response.data.data?.find((c: LcClass) => c.id === selectedClass?.id)
      if (updatedClass) {
        setSelectedClass(updatedClass)
      }
      toast.success(t('class.toast.productUnlinked'))
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  // Class Configuration 저장 mutation
  const saveClassConfigMutation = useMutation({
    mutationFn: (config: LessonConfiguration) =>
      configApi.setClassConfiguration(selectedClass!.id, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuration', 'class', selectedClass?.id] })
      setIsConfigEditModalOpen(false)
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  // Handlers
  const handleCreateClass = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!createFormData.name.trim()) {
      toast.warning(t('class.toast.nameRequired'))
      return
    }
    createClassMutation.mutate(createFormData)
  }

  const handleUpdateClass = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!selectedClass) return
    if (!editFormData.name.trim()) {
      toast.warning(t('class.toast.nameRequired'))
      return
    }
    updateClassMutation.mutate({ id: selectedClass.id, data: editFormData })
  }

  const handleSaveSettings = () => {
    if (!selectedClass) return
    updateClassMutation.mutate({
      id: selectedClass.id,
      data: {
        name: selectedClass.name,
        description: selectedClass.description ?? undefined,
        allowSelfRegistration: classSettings.allowSelfRegistration,
        allowAdminAssignment: classSettings.allowAdminAssignment,
      }
    })
  }

  const handleSaveDeadline = () => {
    if (!selectedClass) return
    updateClassMutation.mutate({
      id: selectedClass.id,
      data: {
        name: selectedClass.name,
        description: selectedClass.description ?? undefined,
        startDate: newStartDate || undefined,
        deadline: newDeadline || undefined,
      }
    })
  }

  const handleSaveMaxStudents = () => {
    if (!selectedClass) return
    if (newMaxStudents < (selectedClass.currentStudents ?? 0)) {
      toast.warning(t('class.toast.capacityTooLow', { count: selectedClass.currentStudents }))
      return
    }
    updateClassMutation.mutate({
      id: selectedClass.id,
      data: {
        name: selectedClass.name,
        description: selectedClass.description ?? undefined,
        maxStudents: newMaxStudents,
      }
    })
  }

  const handleAssignment = () => {
    if (!selectedClass) return

    let userIdsToAdd: number[] = []

    // 클래스 미소속 멤버만 배정 대상
    const availableUserIds = new Set(membersNotInAnyClass.map(m => m.userId))

    if (assignmentTarget === 'all') {
      userIdsToAdd = membersNotInClass
        .filter(m => availableUserIds.has(m.userId))
        .map(m => m.userId)
    } else if (assignmentTarget === 'department' && selectedDepartmentId) {
      userIdsToAdd = membersNotInClass
        .filter(m => m.departmentId === selectedDepartmentId && availableUserIds.has(m.userId))
        .map(m => m.userId)
    } else if (assignmentTarget === 'specialGroup' && selectedSpecialGroupId) {
      userIdsToAdd = selectedUserIds.filter(id => availableUserIds.has(id))
    } else if (assignmentTarget === 'individual') {
      userIdsToAdd = selectedUserIds.filter(id => availableUserIds.has(id))
    }

    if (userIdsToAdd.length === 0) {
      toast.warning(t('class.toast.noAssignable'))
      return
    }

    addMemberMutation.mutate({
      classId: selectedClass.id,
      userIds: userIdsToAdd,
    })
  }

  const handleDeleteClass = () => {
    if (!selectedClass) return
    if (!confirm(t('class.confirmDelete', { name: selectedClass.name }))) return
    deleteClassMutation.mutate(selectedClass.id)
  }

  const resetAssignmentForm = () => {
    setAssignmentTarget('all')
    setSelectedDepartmentId(null)
    setSelectedSpecialGroupId(null)
    setSelectedUserIds([])
    setMemberSearchTerm('')
    setSelectAllChecked(false)
  }

  // 배정 가능한 필터된 멤버 (다른 클래스 미소속)
  const assignableFilteredMembers = filteredMembers.filter(m => !getOtherClassName(m))

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        const newSelection = prev.filter(id => id !== userId)
        setSelectAllChecked(false)
        return newSelection
      } else {
        const newSelection = [...prev, userId]
        if (newSelection.length === assignableFilteredMembers.length) {
          setSelectAllChecked(true)
        }
        return newSelection
      }
    })
  }

  const toggleSelectAll = () => {
    if (selectAllChecked) {
      setSelectedUserIds([])
      setSelectAllChecked(false)
    } else {
      setSelectedUserIds(assignableFilteredMembers.map(m => m.userId))
      setSelectAllChecked(true)
    }
  }

  const handleClassSelect = (cls: LcClass) => {
    setSelectedClass(cls)
    setIsClassSelectorOpen(false)
  }

  const openEditModal = (cls: LcClass) => {
    setEditFormData({
      name: cls.name,
      description: cls.description || '',
      maxStudents: cls.maxStudents ?? 10,
      schedule: cls.schedule || {},
      licenseIds: cls.licenses?.map(l => l.licenseId) || [],
      startDate: cls.startDate || undefined,
      deadline: cls.deadline || undefined,
      allowSelfRegistration: cls.allowSelfRegistration ?? false,
      allowAdminAssignment: cls.allowAdminAssignment ?? true,
    })
    setIsEditModalOpen(true)
  }

  const openSettingsModal = () => {
    if (!selectedClass) return
    setClassSettings({
      allowSelfRegistration: selectedClass.allowSelfRegistration ?? false,
      allowAdminAssignment: selectedClass.allowAdminAssignment ?? true,
    })
    setIsSettingsModalOpen(true)
  }

  const openDeadlineModal = () => {
    if (!selectedClass) return
    setNewStartDate(selectedClass.startDate || '')
    setNewDeadline(selectedClass.deadline || '')
    setIsDeadlineModalOpen(true)
  }

  const openExpandSeatsModal = () => {
    if (!selectedClass) return
    setNewMaxStudents(selectedClass.maxStudents ?? 10)
    setIsExpandSeatsModalOpen(true)
  }

  // Helpers
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getStatusBadge = (cls: LcClass) => {
    if (!cls.isActive) {
      return { label: t('class.status.inactive'), icon: AlertCircle, className: 'bg-gray-100 text-gray-700' }
    }
    
    const now = new Date()
    const deadline = cls.deadline ? new Date(cls.deadline) : null
    
    if (deadline && now > deadline) {
      return { label: t('class.status.closed'), icon: AlertCircle, className: 'bg-red-100 text-red-700' }
    }

    if (deadline) {
      const daysUntilDeadline = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      if (daysUntilDeadline <= 7) {
        return { label: t('class.status.daysUntilDeadline', { days: daysUntilDeadline }), icon: Clock, className: 'bg-orange-100 text-orange-700' }
      }
    }

    return { label: t('class.status.active'), icon: CheckCircle2, className: 'bg-green-100 text-green-700' }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // No class selected - show class list
  if (!selectedClass) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('class.title')}</h1>
            <p className="text-gray-600 mt-1">{t('class.description')}</p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('class.addButton')}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{classes.length}</p>
                <p className="text-sm text-gray-500">{t('class.stats.totalClasses')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mr-4">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {classes.filter(c => c.isActive).length}
                </p>
                <p className="text-sm text-gray-500">{t('class.stats.activeClasses')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mr-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {classes.reduce((sum, c) => sum + (c.currentStudents ?? 0), 0)}
                </p>
                <p className="text-sm text-gray-500">{t('class.stats.totalStudents')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Class List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('class.list.title')}</h2>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-600">{t('class.list.includeInactive')}</span>
            </label>
          </div>

          {classes.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{t('class.empty')}</p>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
              >
                {t('class.firstCreate')}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {classes.map((cls) => {
                const statusBadge = getStatusBadge(cls)
                const StatusIcon = statusBadge.icon
                return (
                  <div
                    key={cls.id}
                    role="button"
                    tabIndex={0}
                    aria-label={t('class.selectLabel', { name: cls.name || t('class.noName') })}
                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedClass(cls)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedClass(cls) }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {cls.name || t('class.noName')}
                          </h3>
                          <div className={clsx(
                            "flex items-center px-2 py-1 rounded-full text-xs font-medium",
                            statusBadge.className
                          )}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusBadge.label}
                          </div>
                        </div>
                        {cls.description && (
                          <p className="text-sm text-gray-500 mt-1 truncate">
                            {cls.description}
                          </p>
                        )}
                        {cls.licenses && cls.licenses.length > 0 && (
                          <p className="text-sm text-gray-500 mt-1 truncate flex items-center">
                            <Package className="w-4 h-4 mr-1 flex-shrink-0" />
                            <span
                              className="text-green-600 hover:text-green-800 hover:underline cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/admin/products/${cls.licenses![0].licenseId}`)
                              }}
                            >
                              {cls.licenses.length === 1
                                ? (cls.licenses[0].productName || t('class.list.productLabel'))
                                : t('class.list.productPlural', { name: cls.licenses[0].productName || t('class.list.productLabel'), count: cls.licenses.length - 1 })}
                            </span>
                            {cls.licenses.some(l => l.curriculumTitle) && (
                              <span className="ml-1 text-gray-400">
                                ({cls.licenses.map(l => l.curriculumTitle).filter(Boolean).join(', ')})
                              </span>
                            )}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {t('class.list.memberCount', { current: cls.currentStudents ?? 0, max: cls.maxStudents ?? 0 })}
                          </span>
                          {(cls.startDate || cls.deadline) && (
                            <span className="flex items-center">
                              <CalendarIcon className="w-4 h-4 mr-1" />
                              {t('class.list.period', { start: '', end: '' }).split(':')[0]}: {cls.startDate ? formatDate(cls.startDate) : '?'} ~ {cls.deadline ? formatDate(cls.deadline) : '?'}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronDown className="w-5 h-5 text-gray-400 -rotate-90" />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Create Class Modal */}
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('class.createModal.title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClass} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('class.createModal.nameLabel')} <span className="text-red-500">{t('class.createModal.nameRequired')}</span>
              </label>
              <input
                type="text"
                value={createFormData.name}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder={t('class.createModal.namePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('class.createModal.descriptionLabel')}
              </label>
              <textarea
                value={createFormData.description}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder={t('class.createModal.descriptionPlaceholder')}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('class.createModal.maxStudentsLabel')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={createFormData.maxStudents === 0 ? '' : createFormData.maxStudents}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '')
                    setCreateFormData(prev => ({ ...prev, maxStudents: val === '' ? 0 : parseInt(val, 10) }))
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('class.createModal.startDateLabel')}
                </label>
                <input
                  type="date"
                  value={createFormData.startDate || ''}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, startDate: e.target.value || undefined }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('class.createModal.deadlineLabel')}
                </label>
                <input
                  type="date"
                  value={createFormData.deadline || ''}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, deadline: e.target.value || undefined }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            {/* 상품 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('class.createModal.productLabel')} <span className="text-gray-400 text-xs">{t('class.createModal.productOptional')}</span>
              </label>
              {licenses.length === 0 ? (
                <p className="text-sm text-gray-500">{t('class.createModal.noProducts')}</p>
              ) : (
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                  <label className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="radio"
                      name="createLicenseId"
                      checked={!createFormData.licenseIds || createFormData.licenseIds.length === 0}
                      onChange={() => {
                        setCreateFormData(prev => {
                          const prevProductName = licenses.find(l => prev.licenseIds?.[0] === l.id)?.productName || ''
                          const isAutoFilled = prev.name === prevProductName
                          return {
                            ...prev,
                            licenseIds: [],
                            ...(isAutoFilled ? { name: '' } : {})
                          }
                        })
                      }}
                      className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-500">{t('class.createModal.noProductSelected')}</span>
                  </label>
                  {licenses.map((license) => (
                    <label
                      key={license.id}
                      className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="createLicenseId"
                        checked={createFormData.licenseIds?.includes(license.id) || false}
                        onChange={() => {
                          setCreateFormData(prev => {
                            // 클래스명이 비어있거나 다른 상품명으로 자동설정된 값이면 선택한 상품명으로 채움
                            const prevProductName = licenses.find(l => prev.licenseIds?.[0] === l.id)?.productName || ''
                            const isAutoFilled = prev.name === '' || prev.name === prevProductName
                            return {
                              ...prev,
                              licenseIds: [license.id],
                              ...(isAutoFilled ? { name: license.productName } : {})
                            }
                          })
                        }}
                        className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{license.productName}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </form>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => handleCreateClass()}
              disabled={createClassMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createClassMutation.isPending ? t('class.createModal.submitting') : t('class.createModal.submit')}
            </button>
          </DialogFooter>
        </DialogContent>
        </Dialog>
      </div>
    )
  }

  // Class selected - show detail view
  const statusBadge = getStatusBadge(selectedClass)
  const StatusIcon = statusBadge.icon
  const hasLinkedProducts = selectedClass.licenses && selectedClass.licenses.length > 0

  return (
    <div className="space-y-6">
      {/* Header with back button and class selector */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setSelectedClass(null)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t('class.detail.backToList')}
        </button>

        {/* Class Selector Dropdown */}
        <div className="relative">
          <button
            onClick={() => setIsClassSelectorOpen(!isClassSelectorOpen)}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <BookOpen className="w-5 h-5 text-gray-400" />
            <span className="font-medium text-gray-700">
              {selectedClass.name || t('class.noName')}
            </span>
            <ChevronDown className={clsx(
              "w-4 h-4 text-gray-400 transition-transform",
              isClassSelectorOpen && "rotate-180"
            )} />
          </button>

          {isClassSelectorOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsClassSelectorOpen(false)}
              />
              <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                <div className="p-2 border-b border-gray-100">
                  <p className="text-xs text-gray-500 px-2">{t('class.detail.selectClass')}</p>
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {classes.map((cls) => (
                    <button
                      key={cls.id}
                      onClick={() => handleClassSelect(cls)}
                      className={clsx(
                        "w-full flex items-center p-3 rounded-lg text-left transition-colors",
                        cls.id === selectedClass.id
                          ? "bg-primary-50 border border-primary-200"
                          : "hover:bg-gray-50"
                      )}
                    >
                      <BookOpen className="w-5 h-5 text-gray-400 mr-3" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {cls.name || t('class.noName')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t('class.list.memberCount', { current: cls.currentStudents ?? 0, max: cls.maxStudents ?? 0 })}
                        </p>
                      </div>
                      {cls.id === selectedClass.id && (
                        <CheckCircle2 className="w-5 h-5 text-primary-600 ml-2" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Class Header Card - 간소화됨 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">
                  {selectedClass.name || t('class.noName')}
                </h1>
                <div className={clsx(
                  "flex items-center px-3 py-1 rounded-full text-sm font-medium",
                  statusBadge.className
                )}>
                  <StatusIcon className="w-4 h-4 mr-1" />
                  {statusBadge.label}
                </div>
              </div>
              {selectedClass.description && (
                <p className="mt-1 text-primary-100 text-sm">
                  {selectedClass.description}
                </p>
              )}
              {/* 연결된 상품 */}
              <div className="mt-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-primary-200" />
                {hasLinkedProducts ? (
                  <>
                    <span className="bg-white/20 px-2 py-0.5 rounded text-sm">
                      {selectedClass.licenses![0].productName || t('class.detail.productNumber', { id: selectedClass.licenses![0].productId })}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedLicenseIds([])
                        setIsProductModalOpen(true)
                      }}
                      className="ml-2 px-3 py-1 bg-yellow-500 hover:bg-yellow-400 text-yellow-900 font-medium rounded text-sm transition-colors shadow-sm"
                    >
                      {t('class.detail.productChange')}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setSelectedLicenseIds([])
                      setIsProductModalOpen(true)
                    }}
                    className="px-3 py-1 bg-yellow-500 hover:bg-yellow-400 text-yellow-900 font-medium rounded text-sm transition-colors shadow-sm flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('class.detail.linkProduct')}
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => openEditModal(selectedClass)}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title={t('class.detail.editTitle')}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={handleDeleteClass}
                className="p-2 bg-white/20 hover:bg-red-500 rounded-lg transition-colors"
                title={t('class.detail.deleteTitle')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 정보 Grid + 액션 버튼 통합 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* 학생 수 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-center mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 text-center">
            {selectedClass.currentStudents ?? 0}
            <span className="text-base font-normal text-gray-500 ml-1">
              / ({selectedClass.maxStudents ?? 0})
            </span>
          </p>
          <p className="text-sm text-gray-500 text-center">{t('class.detail.currentStudents')}</p>
        </div>

        {/* 정원 + 조정 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-center mb-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <Users className="w-4 h-4 text-orange-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 text-center">{selectedClass.maxStudents ?? 0}</p>
          <p className="text-sm text-gray-500 text-center mb-3">{t('class.detail.capacity')}</p>
          <button
            onClick={openExpandSeatsModal}
            className="w-full py-1.5 px-2 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center justify-center"
          >
            <Users className="w-3 h-3 mr-1" />
            {t('class.detail.adjustCapacity')}
          </button>
        </div>

        {/* 설정 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-center mb-2">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Settings className="w-4 h-4 text-purple-600" />
            </div>
          </div>
          <div className="text-center mb-3">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600">
              <span className={clsx(
                "w-2 h-2 rounded-full",
                selectedClass.allowSelfRegistration ? "bg-green-500" : "bg-gray-300"
              )} />
              <span>{t('class.detail.selfRegistration')}: {selectedClass.allowSelfRegistration ? 'O' : 'X'}</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 mt-1">
              <span className={clsx(
                "w-2 h-2 rounded-full",
                selectedClass.allowAdminAssignment ? "bg-green-500" : "bg-gray-300"
              )} />
              <span>{t('class.detail.adminAssignment')}: {selectedClass.allowAdminAssignment ? 'O' : 'X'}</span>
            </div>
          </div>
          <button
            onClick={openSettingsModal}
            className="w-full py-1.5 px-2 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center justify-center"
          >
            <Settings className="w-3 h-3 mr-1" />
            {t('class.detail.changeSettings')}
          </button>
        </div>

        {/* 학습기간 + 수정 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-center mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <CalendarIcon className="w-4 h-4 text-green-600" />
            </div>
          </div>
          <div className="text-center mb-3">
            <p className="text-xs text-gray-500">{t('class.detail.startDate')}</p>
            <p className="text-sm font-medium text-gray-900">
              {selectedClass.startDate ? formatDate(selectedClass.startDate) : t('class.detail.notSet')}
            </p>
            <p className="text-xs text-gray-500 mt-2">{t('class.detail.deadline')}</p>
            <p className="text-sm font-medium text-gray-900">
              {selectedClass.deadline ? formatDate(selectedClass.deadline) : t('class.detail.notSet')}
            </p>
          </div>
          <button
            onClick={openDeadlineModal}
            className="w-full py-1.5 px-2 text-xs bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center"
          >
            <CalendarIcon className="w-3 h-3 mr-1" />
            {t('class.detail.editPeriod')}
          </button>
        </div>
      </div>

      {/* 학습자 목록 (전체 너비) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">
            {t('class.members.title')} ({t('class.members.count', { count: classMembers.length })})
          </h3>
          <button
            onClick={() => setIsAssignmentModalOpen(true)}
            disabled={membersNotInClass.length === 0}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            {t('class.members.assign')}
          </button>
        </div>
        <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {classMembers.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              {t('class.members.empty')}
            </div>
          ) : (
            classMembers.map((member) => {
              // 멤버의 부서 정보 찾기
              const fullMember = allMembers.find(m => m.userId === member.userId)

              return (
                <div key={member.userId} className="px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                      <Users className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{member.fullName}</p>
                        {fullMember?.departmentName && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {fullMember.departmentName}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{member.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm(t('class.members.confirmRemove', { name: member.fullName }))) {
                        removeMemberMutation.mutate({
                          classId: selectedClass.id,
                          userId: member.userId,
                        })
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Class Configuration Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center mr-3">
            <Settings className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{t('class.configuration.title')}</h3>
            <p className="text-sm text-gray-500">{t('class.configuration.description')}</p>
          </div>
        </div>

        {/* 상품 미연결 시 안내 */}
        {!hasLinkedProducts && (
          <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                {t('class.configuration.noProduct')}
              </p>
            </div>
          </div>
        )}

        {/* 설정 소스 안내 메시지 - 상속 체인: Class → License → Product → Curriculum → Default */}
        {hasLinkedProducts && !classConfiguration && licenseConfiguration && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                {t('class.configuration.licenseSource')}
              </p>
            </div>
          </div>
        )}

        {hasLinkedProducts && !classConfiguration && !licenseConfiguration && productConfiguration && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                {t('class.configuration.productSource')}
              </p>
            </div>
          </div>
        )}

        {hasLinkedProducts && !classConfiguration && !licenseConfiguration && !productConfiguration && curriculumConfiguration && (
          <div className="mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-indigo-700">
                {t('class.configuration.curriculumSource')}
              </p>
            </div>
          </div>
        )}

        {hasLinkedProducts && !classConfiguration && !licenseConfiguration && !productConfiguration && !curriculumConfiguration && (
          <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">
                {t('class.configuration.defaultSource')}
              </p>
            </div>
          </div>
        )}

        {hasLinkedProducts && (
          <ConfigurationSummary
            configuration={classConfiguration || licenseConfiguration || productConfiguration || curriculumConfiguration || defaultConfiguration || null}
            onEdit={() => setIsConfigEditModalOpen(true)}
            isLoading={isClassConfigLoading || isLicenseConfigLoading || isProductConfigLoading || isCurriculumConfigLoading || isDefaultConfigLoading}
            isEditDisabled={!(selectedClass?.licenses?.[0]?.allowConfigEdit ?? false)}
            editDisabledReason={t('class.configuration.editDisabledReason')}
          />
        )}
      </div>

      {/* Configuration Edit Modal */}
      {isConfigEditModalOpen && selectedClass && (selectedClass.licenses?.[0]?.allowConfigEdit ?? false) && (
        <ConfigurationEditModal
          targetType="class"
          targetId={selectedClass.id}
          targetName={selectedClass.name || t('class.detail.classNumber', { id: selectedClass.id })}
          configuration={classConfiguration || null}
          parentConfiguration={licenseConfiguration || productConfiguration || curriculumConfiguration || defaultConfiguration || null}
          onClose={() => setIsConfigEditModalOpen(false)}
          onSave={async (config) => {
            await saveClassConfigMutation.mutateAsync(config)
          }}
          isSaving={saveClassConfigMutation.isPending}
        />
      )}

      {/* Settings Modal */}
      <Dialog open={isSettingsModalOpen} onOpenChange={setIsSettingsModalOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('class.settingsModal.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">{t('class.settingsModal.selfRegistrationLabel')}</p>
              <p className="text-sm text-gray-500">{t('class.settingsModal.selfRegistrationDesc')}</p>
            </div>
            <button
              onClick={() => setClassSettings(prev => ({
                ...prev,
                allowSelfRegistration: !prev.allowSelfRegistration
              }))}
              className={clsx(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                classSettings.allowSelfRegistration ? "bg-primary-600" : "bg-gray-200"
              )}
            >
              <span className={clsx(
                "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                classSettings.allowSelfRegistration ? "translate-x-6" : "translate-x-1"
              )} />
            </button>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">{t('class.settingsModal.adminAssignmentLabel')}</p>
              <p className="text-sm text-gray-500">{t('class.settingsModal.adminAssignmentDesc')}</p>
            </div>
            <button
              onClick={() => setClassSettings(prev => ({
                ...prev,
                allowAdminAssignment: !prev.allowAdminAssignment
              }))}
              className={clsx(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                classSettings.allowAdminAssignment ? "bg-primary-600" : "bg-gray-200"
              )}
            >
              <span className={clsx(
                "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                classSettings.allowAdminAssignment ? "translate-x-6" : "translate-x-1"
              )} />
            </button>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => setIsSettingsModalOpen(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={updateClassMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {updateClassMutation.isPending ? t('common.loading') : t('common.save')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* Deadline Modal */}
      <Dialog open={isDeadlineModalOpen} onOpenChange={setIsDeadlineModalOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('class.deadlineModal.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('class.createModal.startDateLabel')}
              </label>
              <input
                type="date"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('class.createModal.deadlineLabel')}
              </label>
              <input
                type="date"
                value={newDeadline}
                onChange={(e) => setNewDeadline(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <p className="text-sm text-gray-500">
            {t('class.deadlineModal.clearNote')}
          </p>
        </div>
        <DialogFooter>
          <button
            onClick={() => setIsDeadlineModalOpen(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSaveDeadline}
            disabled={updateClassMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {updateClassMutation.isPending ? t('common.loading') : t('common.save')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* Assignment Modal */}
      <Dialog open={isAssignmentModalOpen} onOpenChange={(open) => { if (!open) { setIsAssignmentModalOpen(false); resetAssignmentForm() } }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <div>{t('class.assignmentModal.title')}</div>
            <p className="text-sm text-gray-500 mt-1 font-normal">{selectedClass.name}</p>
          </DialogTitle>
        </DialogHeader>
              <div className="space-y-6">
                {/* {t('class.assignmentModal.targetLabel')} */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('class.assignmentModal.targetLabel')}
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <button
                      type="button"
                      onClick={() => setAssignmentTarget('all')}
                      className={clsx(
                        "p-4 rounded-lg border-2 text-center transition-all",
                        assignmentTarget === 'all'
                          ? "border-primary-600 bg-primary-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <Users className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                      <span className="font-medium text-gray-900">{t('class.assignmentModal.allEmployees')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('class.assignmentModal.allAvailable', { count: membersNotInClass.filter(m => !getOtherClassName(m)).length })}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssignmentTarget('department')}
                      className={clsx(
                        "p-4 rounded-lg border-2 text-center transition-all",
                        assignmentTarget === 'department'
                          ? "border-primary-600 bg-primary-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <Building2 className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                      <span className="font-medium text-gray-900">{t('class.assignmentModal.byDepartment')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('class.assignmentModal.departmentCount', { count: departments.length })}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssignmentTarget('specialGroup')}
                      className={clsx(
                        "p-4 rounded-lg border-2 text-center transition-all",
                        assignmentTarget === 'specialGroup'
                          ? "border-primary-600 bg-primary-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <UsersRound className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                      <span className="font-medium text-gray-900">{t('class.assignmentModal.bySpecialGroup')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('class.assignmentModal.groupCount', { count: specialGroups.length })}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssignmentTarget('individual')}
                      className={clsx(
                        "p-4 rounded-lg border-2 text-center transition-all",
                        assignmentTarget === 'individual'
                          ? "border-primary-600 bg-primary-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <UserPlus className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                      <span className="font-medium text-gray-900">{t('class.assignmentModal.individual')}</span>
                      <p className="text-xs text-gray-500 mt-1">{t('class.assignmentModal.multiSelect')}</p>
                    </button>
                  </div>
                </div>

                {/* 부서 선택 */}
                {assignmentTarget === 'department' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('class.assignmentModal.selectDepartment')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedDepartmentId || ''}
                      onChange={(e) => setSelectedDepartmentId(Number(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">{t('class.assignmentModal.selectDepartmentPlaceholder')}</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {t('class.assignmentModal.deptMemberCount', { name: dept.name, count: dept.memberCount })}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* 학습그룹 선택 */}
                {assignmentTarget === 'specialGroup' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('class.assignmentModal.selectSpecialGroup')} <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">{t('class.assignmentModal.groupNote')}</p>
                    <select
                      value={selectedSpecialGroupId || ''}
                      onChange={(e) => {
                        setSelectedSpecialGroupId(Number(e.target.value))
                        setSelectedUserIds([])
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">{t('class.assignmentModal.selectSpecialGroupPlaceholder')}</option>
                      {specialGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {t('class.assignmentModal.deptMemberCount', { name: group.name, count: group.memberCount })}
                        </option>
                      ))}
                    </select>

                    {/* 그룹 멤버 목록 */}
                    {selectedSpecialGroupId && (() => {
                      const groupMembers: SpecialGroupMember[] = specialGroupMembersData?.data?.data || []
                      const notInCurrentClass = groupMembers.filter(
                        gm => membersNotInClass.some(m => m.userId === gm.userId)
                      )
                      // 다른 클래스 미소속 멤버만 실제 배정 가능
                      const assignableMembers = notInCurrentClass.filter(gm => {
                        const member = allMembers.find(m => m.userId === gm.userId)
                        return !member?.classNames || member.classNames.length === 0
                      })
                      const inOtherClass = notInCurrentClass.filter(gm => {
                        const member = allMembers.find(m => m.userId === gm.userId)
                        return member?.classNames && member.classNames.length > 0
                      })
                      const alreadyAssigned = groupMembers.filter(
                        gm => classMembers.some(cm => cm.userId === gm.userId)
                      )
                      const allAssignableSelected = assignableMembers.length > 0 &&
                        assignableMembers.every(m => selectedUserIds.includes(m.userId))
                      return (
                        <div className="mt-3">
                          <p className="text-sm text-gray-600 mb-2">
                            {t('class.assignmentModal.assignable', { count: assignableMembers.length })}
                            {selectedUserIds.length > 0 && (
                              <span className="font-medium text-primary-600 ml-1">{t('class.assignmentModal.selected', { count: selectedUserIds.length })}</span>
                            )}
                            {alreadyAssigned.length > 0 && (
                              <span className="text-gray-400 ml-2">{t('class.assignmentModal.alreadyAssigned', { count: alreadyAssigned.length })}</span>
                            )}
                            {inOtherClass.length > 0 && (
                              <span className="text-amber-600 ml-2">{t('class.assignmentModal.inOtherClass', { count: inOtherClass.length })}</span>
                            )}
                          </p>
                          {notInCurrentClass.length > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              {assignableMembers.length > 0 && (
                                <div className="flex items-center p-3 bg-gray-50 border-b border-gray-200">
                                  <label className="flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={allAssignableSelected}
                                      onChange={() => {
                                        if (allAssignableSelected) {
                                          setSelectedUserIds([])
                                        } else {
                                          setSelectedUserIds(assignableMembers.map(m => m.userId))
                                        }
                                      }}
                                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                    />
                                    <span className="ml-3 text-sm font-medium text-gray-700">
                                      {t('class.assignmentModal.selectAll', { count: assignableMembers.length })}
                                    </span>
                                  </label>
                                </div>
                              )}
                              <div className="max-h-48 overflow-y-auto">
                                {notInCurrentClass.map((member) => {
                                  const fullMember = allMembers.find(m => m.userId === member.userId)
                                  const otherClass = fullMember ? getOtherClassName(fullMember) : null
                                  return (
                                    <label
                                      key={member.userId}
                                      className={clsx(
                                        "flex items-center p-3 border-b border-gray-100 last:border-0",
                                        otherClass
                                          ? "opacity-50 cursor-not-allowed bg-gray-50"
                                          : "hover:bg-gray-50 cursor-pointer"
                                      )}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedUserIds.includes(member.userId)}
                                        onChange={() => {
                                          if (!otherClass) {
                                            setSelectedUserIds(prev =>
                                              prev.includes(member.userId)
                                                ? prev.filter(id => id !== member.userId)
                                                : [...prev, member.userId]
                                            )
                                          }
                                        }}
                                        disabled={!!otherClass}
                                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50"
                                      />
                                      <div className="ml-3 flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <p className="text-sm font-medium text-gray-900">{member.fullName}</p>
                                          {otherClass && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                              {t('class.assignmentModal.otherClassLabel', { name: otherClass })}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500">{member.email}</p>
                                      </div>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                          {assignableMembers.length === 0 && groupMembers.length > 0 && (
                            <p className="text-sm text-gray-500">{t('class.assignmentModal.allAssigned')}</p>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                )}

                {/* 개인 선택 */}
                {assignmentTarget === 'individual' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('class.assignmentModal.selectLearner')} <span className="text-red-500">*</span>
                        {selectedUserIds.length > 0 && (
                          <span className="ml-2 text-primary-600">
                            {t('class.assignmentModal.selected', { count: selectedUserIds.length })}
                          </span>
                        )}
                      </label>
                    </div>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={memberSearchTerm}
                        onChange={(e) => setMemberSearchTerm(e.target.value)}
                        placeholder={t('class.assignmentModal.searchPlaceholder')}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* 전체 선택 헤더 */}
                      <div className="flex items-center p-3 bg-gray-50 border-b border-gray-200">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectAllChecked}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            {t('class.assignmentModal.selectAllAssignable', { count: assignableFilteredMembers.length })}
                          </span>
                        </label>
                      </div>
                      {/* 학습자 목록 */}
                      <div className="max-h-64 overflow-y-auto">
                        {filteredMembers.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            {memberSearchTerm ? t('class.assignmentModal.noSearchResult') : t('class.assignmentModal.noAvailable')}
                          </div>
                        ) : (
                          filteredMembers.map((member) => {
                            const otherClass = getOtherClassName(member)
                            return (
                              <label
                                key={member.userId}
                                className={clsx(
                                  "flex items-center p-3 border-b border-gray-100 last:border-0",
                                  otherClass
                                    ? "opacity-50 cursor-not-allowed bg-gray-50"
                                    : "hover:bg-gray-50 cursor-pointer"
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedUserIds.includes(member.userId)}
                                  onChange={() => !otherClass && toggleUserSelection(member.userId)}
                                  disabled={!!otherClass}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50"
                                />
                                <div className="ml-3 flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-900">{member.fullName}</p>
                                    {otherClass && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                        {t('class.assignmentModal.otherClassLabel', { name: otherClass })}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">{member.email}</p>
                                </div>
                              </label>
                            )
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
        <DialogFooter>
          <button
            onClick={() => {
              setIsAssignmentModalOpen(false)
              resetAssignmentForm()
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleAssignment}
            disabled={
              addMemberMutation.isPending ||
              (assignmentTarget === 'department' && !selectedDepartmentId) ||
              (assignmentTarget === 'specialGroup' && (!selectedSpecialGroupId || selectedUserIds.length === 0)) ||
              (assignmentTarget === 'individual' && selectedUserIds.length === 0)
            }
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {addMemberMutation.isPending ? t('class.assignmentModal.submitting') : t('class.assignmentModal.submit')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* Cancel Assignment Modal */}
      <Dialog open={isCancelAssignmentModalOpen} onOpenChange={setIsCancelAssignmentModalOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('class.cancelAssignmentModal.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-gray-600 mb-4">
          {t('class.cancelAssignmentModal.description')}
        </p>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {classMembers.map((member) => (
            <div
              key={member.userId}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                  <Users className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{member.fullName}</p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (confirm(t('class.members.confirmRemove', { name: member.fullName }))) {
                    removeMemberMutation.mutate({
                      classId: selectedClass.id,
                      userId: member.userId,
                    })
                  }
                }}
                disabled={removeMemberMutation.isPending}
                className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <button
            onClick={() => setIsCancelAssignmentModalOpen(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            {t('common.close')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* Expand Seats Modal */}
      <Dialog open={isExpandSeatsModalOpen} onOpenChange={setIsExpandSeatsModalOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('class.expandSeatsModal.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('class.expandSeatsModal.currentCapacity')}</span>
                <span className="font-medium text-gray-900">{t('class.expandSeatsModal.currentCount', { count: selectedClass.maxStudents ?? 0 })}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-600">{t('class.expandSeatsModal.currentStudents')}</span>
                <span className="font-medium text-gray-900">{t('class.expandSeatsModal.currentCount', { count: selectedClass.currentStudents ?? 0 })}</span>
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('class.expandSeatsModal.newCapacity')}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={newMaxStudents === 0 ? '' : newMaxStudents}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '')
                setNewMaxStudents(val === '' ? 0 : parseInt(val, 10))
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-sm text-orange-600 mt-2">
              {t('class.expandSeatsModal.minNote', { count: selectedClass.currentStudents ?? 0 })}
            </p>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => setIsExpandSeatsModalOpen(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSaveMaxStudents}
            disabled={updateClassMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {updateClassMutation.isPending ? t('common.loading') : t('common.save')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* Edit Class Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('class.detail.editTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleUpdateClass} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('class.createModal.nameLabel')} <span className="text-red-500">{t('class.createModal.nameRequired')}</span>
            </label>
            <input
              type="text"
              value={editFormData.name}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder={t('class.createModal.namePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('class.createModal.descriptionLabel')}
            </label>
            <textarea
              value={editFormData.description}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder={t('class.createModal.descriptionPlaceholder')}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('class.createModal.startDateLabel')}
              </label>
              <input
                type="date"
                value={editFormData.startDate || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, startDate: e.target.value || undefined }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('class.createModal.deadlineLabel')}
              </label>
              <input
                type="date"
                value={editFormData.deadline || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, deadline: e.target.value || undefined }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </form>
        <DialogFooter>
          <button
            type="button"
            onClick={() => setIsEditModalOpen(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleUpdateClass}
            disabled={updateClassMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {updateClassMutation.isPending ? t('common.loading') : t('common.save')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* Product Modal - 연결 상품 설정 (1개만 가능) */}
      <Dialog open={isProductModalOpen} onOpenChange={setIsProductModalOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('class.productModal.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* {t('class.productModal.currentProduct')} */}
          {selectedClass.licenses && selectedClass.licenses.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <Package className="w-4 h-4 mr-2 text-primary-600" />
                {t('class.productModal.currentProduct')}
              </h3>
              <div className="flex items-center justify-between p-4 border-2 border-primary-200 bg-primary-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {selectedClass.licenses[0].productName || t('class.detail.productNumber', { id: selectedClass.licenses[0].productId })}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {selectedClass.licenses[0].curriculumTitle || t('class.productModal.curriculumNotLinked')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if ((selectedClass.currentStudents ?? 0) > 0) {
                      toast.warning(t('class.productModal.unlinkWarning'))
                      return
                    }
                    const license = selectedClass.licenses![0]
                    if (confirm(t('class.productModal.confirmUnlink', { name: license.productName || t('class.detail.productNumber', { id: license.productId }) }))) {
                      removeProductMutation.mutate({
                        classId: selectedClass.id,
                        licenseIds: [license.licenseId],
                      })
                    }
                  }}
                  disabled={removeProductMutation.isPending}
                  className="ml-3 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 flex-shrink-0"
                >
                  {removeProductMutation.isPending ? t('class.productModal.unlinking') : t('class.productModal.unlink')}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-400 flex items-center">
                <Info className="w-3 h-3 mr-1" />
                {t('class.productModal.changeNote')}
              </p>
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <Package className="w-4 h-4 mr-2 text-primary-600" />
                {t('class.productModal.selectProduct')} <span className="text-gray-400 text-xs ml-1">{t('class.productModal.selectProductNote')}</span>
              </h3>
              {availableLicenses.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-gray-500 text-sm">{t('class.productModal.noProducts')}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2">
                    {availableLicenses.map((license) => (
                      <label
                        key={license.id}
                        className={clsx(
                          "flex items-center p-3 rounded-lg cursor-pointer transition-colors",
                          selectedLicenseIds.includes(license.id)
                            ? "bg-primary-50 border border-primary-300"
                            : "hover:bg-gray-50 border border-transparent"
                        )}
                      >
                        <input
                          type="radio"
                          name="productLicenseId"
                          checked={selectedLicenseIds.includes(license.id)}
                          onChange={() => {
                            setSelectedLicenseIds([license.id])
                          }}
                          className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                        />
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{license.productName}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {license.curriculumTitle || t('class.productModal.curriculumNotLinked')}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {selectedLicenseIds.length > 0 && (
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => {
                          addProductMutation.mutate({
                            classId: selectedClass.id,
                            licenseIds: selectedLicenseIds,
                          })
                        }}
                        disabled={addProductMutation.isPending}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
                      >
                        {addProductMutation.isPending ? t('class.productModal.linking') : t('class.productModal.link')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={() => setIsProductModalOpen(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            {t('common.close')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>
    </div>
  )
}
